import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Loader2, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { exportarResumenNominas } from './utils/nominaExcel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getNominas, crearNomina, getParametrosNomina, getSedesNomina } from './Services/nomina.services';
import {
  ESTADOS,
  TIPOS_NOMINA,
  fechaCorta,
  pesos,
  nombreSugerido,
  periodoPorTipo,
  horas,
  sumaGrupo,
} from './utils/nomina';

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** "1–15 oct 2026"; si cambia de mes, "16 sep – 15 oct 2026". */
const periodoCorto = (desde, hasta) => {
  const [a1, m1, d1] = String(desde).split('-').map(Number);
  const [a2, m2, d2] = String(hasta).split('-').map(Number);
  if (a1 === a2 && m1 === m2) return `${d1}–${d2} ${MESES_CORTOS[m1 - 1]} ${a1}`;
  return `${d1} ${MESES_CORTOS[m1 - 1]}${a1 !== a2 ? ` ${a1}` : ''} – ${d2} ${MESES_CORTOS[m2 - 1]} ${a2}`;
};

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const formInicial = (parametros) => {
  const { desde, hasta } = periodoPorTipo('quincenal', hoyISO());
  return {
    tipo: 'quincenal',
    desde,
    hasta,
    nombre: nombreSugerido(desde, hasta, 'quincenal'),
    nombreEditado: false,
    horasBase: parametros?.horasBaseQuincenal ?? 90,
    todasLasSedes: true,
    centros: [],
  };
};

const NominaListPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState('todas');
  const [parametros, setParametros] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(formInicial(null));
  const [creando, setCreando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      setItems(await getNominas({ estado }));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [estado]);

  useEffect(() => {
    getParametrosNomina().then(setParametros).catch(() => {});
    getSedesNomina()
      .then(setSedes)
      .catch(() => setSedes([]));
  }, []);

  const horasBasePara = (tipo, desde, hasta) => {
    if (tipo === 'quincenal') return parametros?.horasBaseQuincenal ?? 90;
    const mensual = parametros?.horasBaseMensual ?? 180;
    if (tipo === 'mensual' || !desde || !hasta) return mensual;
    const dias = Math.round((Date.parse(`${hasta}T12:00:00Z`) - Date.parse(`${desde}T12:00:00Z`)) / 86400000) + 1;
    return Math.round(((mensual * Math.min(30, Math.max(1, dias))) / 30) * 10) / 10;
  };

  // Cambiar tipo o fechas recalcula nombre (si no se editó a mano) y horas base.
  const actualizar = (cambios) => {
    setForm((f) => {
      let sig = { ...f, ...cambios };
      if (cambios.tipo && cambios.tipo !== 'otro') {
        sig = { ...sig, ...periodoPorTipo(cambios.tipo, f.desde || hoyISO()) };
      }
      if (cambios.tipo || cambios.desde || cambios.hasta) {
        sig.horasBase = horasBasePara(sig.tipo, sig.desde, sig.hasta);
        if (!sig.nombreEditado) sig.nombre = nombreSugerido(sig.desde, sig.hasta, sig.tipo);
      }
      return sig;
    });
  };

  const abrirNueva = () => {
    setForm(formInicial(parametros));
    setOpen(true);
  };

  const crear = async (e) => {
    e.preventDefault();
    if (!form.todasLasSedes && form.centros.length === 0) {
      toast({ title: 'Sedes', description: 'Elige al menos una sede o marca todas.', variant: 'destructive' });
      return;
    }
    setCreando(true);
    try {
      const nomina = await crearNomina({
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        desde: form.desde,
        hasta: form.hasta,
        horasBase: Number(form.horasBase),
        centros: form.todasLasSedes ? [] : form.centros,
        creadoPor: { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email },
      });
      toast({ title: 'Nómina generada', description: `${nomina.totales?.personas ?? 0} personas en ${nomina.nombre}.` });
      setOpen(false);
      navigate(`/personal/nomina/${nomina._id}`);
    } catch (err) {
      toast({ title: 'No se generó', description: err.message, variant: 'destructive' });
    } finally {
      setCreando(false);
    }
  };

  const alternarSede = (sede) =>
    setForm((f) => ({
      ...f,
      centros: f.centros.some((c) => c.id === sede.id) ? f.centros.filter((c) => c.id !== sede.id) : [...f.centros, sede],
    }));

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={loading || items.length === 0}
              onClick={() =>
                exportarResumenNominas(items, { filtro: estado }).catch((e) =>
                  toast({ title: 'No se exportó', description: e.message, variant: 'destructive' }),
                )
              }
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Exportar Excel
            </Button>
            <Button onClick={abrirNueva}>
              <Plus className="mr-2 h-4 w-4" /> Nueva nómina
            </Button>
          </div>
        </div>

        <div className="rounded-lg border bg-card overflow-x-auto">
          <Table className="[&_td]:px-2 [&_th]:px-2">
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Personas</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right" title="Promedio del valor hora de las personas de la nómina">Valor hora</TableHead>
                <TableHead className="text-right">Recargos</TableHead>
                <TableHead className="text-right">Extras</TableHead>
                <TableHead className="text-right" title="Básico + auxilio + recargos + extras, antes de deducciones">Devengado</TableHead>
                <TableHead className="text-right" title="Devengado menos salud y pensión: lo que recibe la persona">Neto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground">Cargando...</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground">
                    Todavía no hay nóminas. Crea la primera con «Nueva nómina».
                  </TableCell>
                </TableRow>
              ) : (
                items.map((n) => (
                  <TableRow key={n._id} className="cursor-pointer" onClick={() => navigate(`/personal/nomina/${n._id}`)}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {n.nombre}
                      {n.estado === 'borrador' && n.pendientesAutorizar > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle className="h-3 w-3" /> {n.pendientesAutorizar} pendientes de autorizar
                        </div>
                      )}
                      {n.desactualizada && (
                        <div
                          className="flex items-center gap-1 text-xs text-rose-600"
                          title={`Hay fichas con un salario distinto al usado en el último cálculo:\n${n.desactualizada.ejemplos.join('\n')}`}
                        >
                          <AlertTriangle className="h-3 w-3" /> {n.desactualizada.personas} salario(s) cambiaron · recalcular
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap" title={`${TIPOS_NOMINA[n.tipo] || n.tipo}: ${fechaCorta(n.desde)} – ${fechaCorta(n.hasta)}`}>
                      {periodoCorto(n.desde, n.hasta)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ESTADOS[n.estado]?.variant}>{ESTADOS[n.estado]?.label || n.estado}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{n.totales?.personas ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(n.totales?.horas?.total || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{n.horasBase} h</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {n.valorHoraPromedio ? pesos(n.valorHoraPromedio) : '—'}
                      <div className="text-xs text-muted-foreground">promedio</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {pesos(n.totales?.valores?.recargos)}
                      <div className="text-xs text-muted-foreground">{horas(sumaGrupo(n.totales?.horas, 'Recargos'))} h</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {pesos(n.totales?.valores?.extras)}
                      <div className="text-xs text-muted-foreground">{horas(sumaGrupo(n.totales?.horas, 'Extras'))} h</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(n.totales?.devengado)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">{pesos(n.totales?.neto)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !creando && setOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva nómina</DialogTitle>
            <DialogDescription>
              Se traen todas las personas con marcaciones, incapacidades o permisos en el periodo, con sus horas
              ordinarias, extras, nocturnas, festivas y dominicales.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={crear} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => actualizar({ tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPOS_NOMINA).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom-base">Horas base por persona</Label>
                <Input
                  id="nom-base"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.horasBase}
                  onChange={(e) => setForm({ ...form, horasBase: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="nom-desde">Desde</Label>
                <Input id="nom-desde" type="date" value={form.desde} onChange={(e) => actualizar({ desde: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom-hasta">Hasta</Label>
                <Input id="nom-hasta" type="date" value={form.hasta} min={form.desde} onChange={(e) => actualizar({ hasta: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nom-nombre">Nombre</Label>
              <Input
                id="nom-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value, nombreEditado: true })}
                placeholder="quincena-1-sep-2026"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Las horas que superen las horas base, en orden de fecha, se liquidan como extras (las de domingo o
              festivo, como extras dominicales). Las incapacidades y permisos remunerados cuentan dentro de la base.
              Referencia: (días de la quincena − domingos) × 7 h, p. ej. 91 h.
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.todasLasSedes} onCheckedChange={(v) => setForm({ ...form, todasLasSedes: !!v })} />
                Todas las sedes
              </label>
              {!form.todasLasSedes && (
                <div className="max-h-40 overflow-y-auto rounded-md border p-2 grid grid-cols-2 gap-1">
                  {sedes.length === 0 && (
                    <p className="col-span-2 text-sm text-muted-foreground">No se encontraron sedes activas.</p>
                  )}
                  {sedes.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.centros.some((c) => c.id === s.id)} onCheckedChange={() => alternarSede(s)} />
                      <span className="truncate">{s.nombre}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={creando}>Cancelar</Button>
              <Button type="submit" disabled={creando}>
                {creando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generando…
                  </>
                ) : (
                  'Generar nómina'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default NominaListPage;
